import { useEffect, useRef, useState } from 'react';
import { Upload, Check } from 'lucide-react';
import { spinnerRing } from '../utils/bookingUtils';
import { formatCountdown, msUntil } from '../utils/countdownUtil';
import { PAYMENT_STEP_LABELS } from '../constants/bookingConst';
import '../css/PosCheckout.css';

/**
 * The checkout card shown in the checkout tab — the court-booking system's
 * CheckoutSummary, shared by the till's renewal and the landing page's
 * sign-up: what is being bought, the total, STEP 1 pay with our QR, STEP 2
 * upload the receipt and submit.
 *
 * A card on a page, not a dialog: it is the whole of its own tab, so the
 * PAGE scrolls and the card is as tall as its content.
 *
 * The receipt is sent with Submit rather than the moment it is picked, so a
 * wrong picture can still be replaced first.
 *
 * @param {object} props
 * @param {string} props.title - the card heading
 * @param {Array<{key: string, title: string, label?: string, rows: Array<{key: string, label: string, amount: string}>}>} props.boxes
 *   - the itemised order, one bordered box per group
 * @param {string} props.total - the formatted total
 * @param {React.ReactNode} props.instructions - how to pay, in the tinted box under STEP 1
 * @param {number|null} [props.deadline] - epoch ms the payment window closes; null for none
 * @param {() => void} [props.onExpire] - called once when the deadline passes
 * @param {{label: string, options: Array<{key: string, label: string}>, value: string, onChange: (key: string) => void, hint?: string}} [props.payPicker]
 *   - a choice above the QR (the landing page's payment app)
 * @param {string} [props.payTo] - the account the QR pays, as a fixed label (the till)
 * @param {string} props.qrUrl - '' when no QR is set up
 * @param {string} props.noQrMessage - shown in place of the QR when there is none
 * @param {string} props.submitLabel
 * @param {boolean} props.submitting
 * @param {string} [props.error] - why the last submit failed
 * @param {(receiptFile: File) => void} props.onSubmit
 * @param {() => void} props.onClose - the ×: back out of the checkout
 * @param {string} props.closeLabel - what the × does, for screen readers
 * @returns {JSX.Element}
 */
function PosCheckoutSummary({
  title, boxes, total, instructions, deadline = null, onExpire, payPicker, payTo = '',
  qrUrl, noQrMessage, submitLabel, submitting, error = '', onSubmit, onClose, closeLabel,
}) {
  const fileInputRef = useRef(null);
  const [receiptFile, setReceiptFile] = useState(null);
  const [msLeft, setMsLeft] = useState(() => msUntil(deadline));
  const expiredRef = useRef(false);
  const onExpireRef = useRef(onExpire);
  onExpireRef.current = onExpire;

  // Live countdown, when there is a payment window. Stops once submitting —
  // a receipt that is already on its way is not cut off by the clock.
  useEffect(() => {
    if (!deadline || submitting) return undefined;
    const tick = () => {
      const remaining = msUntil(deadline);
      setMsLeft(remaining);
      if (remaining !== null && remaining <= 0 && !expiredRef.current) {
        expiredRef.current = true;
        onExpireRef.current?.();
      }
    };
    tick();
    const interval = setInterval(tick, 1000);
    return () => clearInterval(interval);
  }, [deadline, submitting]);

  const expired = msLeft !== null && msLeft <= 0;

  /**
   * Takes the chosen receipt image. Compressed when the order is sent.
   * @param {React.ChangeEvent<HTMLInputElement>} event
   * @returns {void}
   */
  const handlePick = (event) => {
    const file = event.target.files?.[0];
    // Cleared so picking the same file again (after replacing it) still fires.
    event.target.value = '';
    if (file) setReceiptFile(file);
  };

  return (
    <div className="ps-co">
      <div className="ps-co-head">
        <h2 className="ps-co-title">{title}</h2>
        <button
          type="button"
          className="ps-co-close"
          onClick={onClose}
          disabled={submitting}
          aria-label={closeLabel}
          title={closeLabel}
        >
          &times;
        </button>
      </div>

      {boxes.map((box) => (
        <div className="ps-co-box" key={box.key}>
          <h3>{box.title}</h3>
          {box.label && <div className="ps-co-lbl">{box.label}</div>}
          {box.rows.map((row) => (
            <div className="ps-co-row" key={row.key}>
              <span className="ps-co-item">{row.label}</span>
              <span className="ps-co-fee">{row.amount}</span>
            </div>
          ))}
        </div>
      ))}

      <div className="ps-co-total">
        <span>Total</span>
        <span className="ps-co-total-amt">{total}</span>
      </div>

      <div className="ps-co-step" role="heading" aria-level={3}>{PAYMENT_STEP_LABELS.PAY}</div>

      {deadline && (
        <div className={`ps-co-info${expired ? ' ps-co-info-out' : ''}`}>
          {expired
            ? 'This checkout has expired. Close it and start again when you are ready to pay.'
            : <>Complete payment within <strong>{formatCountdown(msLeft)}</strong> or this request expires.</>}
        </div>
      )}
      <div className="ps-co-info">{instructions}</div>

      {payPicker && (
        <>
          <div className="ps-co-qr-label">{payPicker.label}</div>
          <select
            className="ps-co-select"
            value={payPicker.value}
            onChange={(event) => payPicker.onChange(event.target.value)}
            disabled={submitting}
          >
            {payPicker.options.map((option) => <option key={option.key} value={option.key}>{option.label}</option>)}
          </select>
        </>
      )}
      {!payPicker && payTo && (
        <>
          <div className="ps-co-qr-label">Pay to</div>
          <div className="ps-co-account">{payTo}</div>
        </>
      )}

      {qrUrl ? (
        <>
          <div className="ps-co-qr-card">
            <img src={qrUrl} alt="Payment QR code" />
            {payPicker && payTo && <b className="ps-co-qr-name">{payTo}</b>}
            {payPicker?.hint && <span className="ps-co-qr-hint">{payPicker.hint}</span>}
          </div>
          <p className="ps-co-note">
            Before you send, please double-check you&rsquo;re paying to the account shown in the QR
            above — just to be sure your payment goes to the right account.
          </p>
        </>
      ) : (
        <p className="ps-co-note">{noQrMessage}</p>
      )}

      <div className="ps-co-step" role="heading" aria-level={3}>{PAYMENT_STEP_LABELS.UPLOAD}</div>

      <input
        type="file"
        accept="image/png,image/jpeg,image/webp"
        ref={fileInputRef}
        onChange={handlePick}
        hidden
      />
      <button
        type="button"
        className={`ps-co-upload${receiptFile ? ' ps-co-upload-done' : ''}`}
        onClick={() => fileInputRef.current?.click()}
        disabled={submitting || expired}
      >
        {receiptFile
          ? <><Check size={16} /> Receipt attached — {receiptFile.name}</>
          : <><Upload size={16} /> Upload Payment Receipt</>}
      </button>
      {receiptFile && !submitting && !expired && <p className="ps-co-hint">Tap the receipt above to replace it.</p>}

      {submitting && <div className="ps-co-sending">{spinnerRing('Submitting')}</div>}
      {error && <p className="ps-co-error">{error}</p>}

      <button
        type="button"
        className="ps-co-submit"
        disabled={!receiptFile || submitting || expired}
        onClick={() => onSubmit(receiptFile)}
      >
        {submitting ? 'Submitting…' : submitLabel}
      </button>
    </div>
  );
}

export default PosCheckoutSummary;
